import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Lock, Zap, ChevronRight, RefreshCw, Eye } from 'lucide-react';

/* ── Palette ─────────────────────────────────────────────────────────────── */
const PLAN_COLORS = {
  Business:     { bg: '#477242', light: 'rgba(71,114,66,0.15)', text: '#6abf64' },
  Professional: { bg: '#C85A32', light: 'rgba(200,90,50,0.15)',  text: '#e8855e' },
  Enterprise:   { bg: '#2D3728', light: 'rgba(45,55,40,0.25)',   text: '#a3b899' },
};

/* ── Sample data ─────────────────────────────────────────────────────────── */
const SAMPLE = {
  products: [
    { name: 'NPK Fertilizer 14-14-14 (50kg)', sku: 'FERT-NPK-50', stock: 48, price: 1560 },
    { name: 'Glyphosate 480SL (1L)',           sku: 'HERB-GLYPH', stock: 22, price: 455  },
    { name: 'Cypermethrin 10EC (500ml)',        sku: 'INSC-CYP',  stock: 35, price: 364  },
    { name: 'Mancozeb 80WP (500g)',             sku: 'FUNG-MNCZ', stock: 61, price: 325  },
  ],
  branches: ['Main Branch', 'Warehouse', 'South Branch'],
  customers: ['Juan dela Cruz', 'Maria Santos', 'Pedro Reyes', 'Farmers Coop Titay'],
  employees: [
    { name: 'Ana Reyes',     role: 'Cashier',  advance: 2500,  limit: 5000 },
    { name: 'Ben Santos',    role: 'Cashier',  advance: 0,     limit: 5000 },
    { name: 'Carlo Mendoza', role: 'Manager',  advance: 8000,  limit: 15000 },
  ],
};

/* ── Shared preview wrapper ─────────────────────────────────────────────── */
function PreviewPage({ children }) {
  return (
    <div className="relative min-h-full">
      {/* Write-block overlay on interactive elements */}
      {children}
      {/* Subtle "actions blocked" sheen on button areas */}
    </div>
  );
}

function MockBtn({ label, primary = false, sm = false }) {
  return (
    <button
      className={`rounded-lg font-semibold select-none cursor-default relative group ${
        sm ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm'
      } ${primary
        ? 'bg-emerald-700/50 text-emerald-300 border border-emerald-700/40'
        : 'bg-white/5 text-slate-400 border border-white/10'
      }`}
      onClick={e => e.preventDefault()}
    >
      {label}
      <span className="absolute inset-0 rounded-lg group-hover:bg-white/5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Lock size={12} className="text-amber-400" />
      </span>
    </button>
  );
}

function TableHeader({ cols }) {
  return (
    <tr className="border-b border-white/5">
      {cols.map(c => (
        <th key={c} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
          {c}
        </th>
      ))}
    </tr>
  );
}

/* ── Feature Previews ───────────────────────────────────────────────────── */

function PurchaseOrdersPreview() {
  const [activeTab, setActiveTab] = useState('list');
  const pos = [
    { id: 'PO-MB-001007', supplier: 'AgroSupply Corp.',  items: 3, total: 72500, status: 'received',  date: 'May 18' },
    { id: 'PO-MB-001008', supplier: 'BioChemPest Inc.',  items: 2, total: 18200, status: 'pending',   date: 'May 22' },
    { id: 'PO-MB-001009', supplier: 'PhilFarm Distrib.', items: 5, total: 41600, status: 'confirmed', date: 'May 25' },
  ];
  const STATUS = { received: 'bg-emerald-500/20 text-emerald-400', pending: 'bg-amber-500/20 text-amber-400', confirmed: 'bg-blue-500/20 text-blue-400' };

  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-white font-bold text-base">Purchase Orders</h2>
          <p className="text-slate-400 text-xs mt-0.5">Track stock purchases from your suppliers</p>
        </div>
        <MockBtn label="+ New Purchase Order" primary />
      </div>
      <div className="flex gap-2 mb-4">
        {['list', 'create'].map(t => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${activeTab === t ? 'bg-white/10 text-white' : 'text-slate-500 hover:text-slate-300'}`}
          >
            {t === 'list' ? 'PO List' : 'Create New PO'}
          </button>
        ))}
      </div>

      {activeTab === 'list' ? (
        <div className="bg-slate-900/60 rounded-xl overflow-hidden border border-white/5">
          <table className="w-full text-sm">
            <thead><TableHeader cols={['PO Number', 'Supplier', 'Items', 'Total', 'Status', 'Date']} /></thead>
            <tbody>
              {pos.map(po => (
                <tr key={po.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-3 py-2.5 text-emerald-400 font-mono text-xs font-bold">{po.id}</td>
                  <td className="px-3 py-2.5 text-slate-300 text-xs">{po.supplier}</td>
                  <td className="px-3 py-2.5 text-slate-400 text-xs">{po.items}</td>
                  <td className="px-3 py-2.5 text-white font-semibold text-xs" style={{ fontVariantNumeric: 'tabular-nums' }}>₱{po.total.toLocaleString()}</td>
                  <td className="px-3 py-2.5">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${STATUS[po.status]}`}>{po.status}</span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-500 text-xs">{po.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-slate-900/60 rounded-xl p-5 border border-white/5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Supplier</label>
              <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm">AgroSupply Corporation</div>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Expected Delivery</label>
              <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm">Jun 5, 2026</div>
            </div>
          </div>
          <div className="bg-slate-800/60 rounded-xl overflow-hidden border border-white/5">
            <div className="px-4 py-2.5 border-b border-white/5 text-xs font-semibold text-slate-400 flex justify-between">
              <span>Products</span>
              <MockBtn label="+ Add Product" sm />
            </div>
            {SAMPLE.products.slice(0, 2).map(p => (
              <div key={p.sku} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5">
                <div className="flex-1">
                  <p className="text-white text-xs font-medium">{p.name}</p>
                  <p className="text-slate-500 text-[10px]">{p.sku} · Current stock: {p.stock}</p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="bg-white/5 border border-white/10 rounded-md px-2 py-1 text-slate-300 text-xs w-16 text-center">20</div>
                  <div className="text-slate-400 text-xs">× ₱{p.price.toLocaleString()}</div>
                  <div className="text-white text-xs font-bold w-20 text-right">₱{(20 * p.price).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-between items-center">
            <span className="text-slate-500 text-xs">Total: <strong className="text-white">₱{(20 * 1560 + 20 * 455).toLocaleString()}</strong></span>
            <div className="flex gap-2">
              <MockBtn label="Save Draft" />
              <MockBtn label="Confirm PO" primary />
            </div>
          </div>
        </div>
      )}
    </PreviewPage>
  );
}

function BranchTransfersPreview() {
  const [step, setStep] = useState(0);
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-base">Branch Transfer</h2>
        <div className="flex items-center gap-2">
          {['Select Products', 'Confirm', 'Done'].map((s, i) => (
            <div key={s} className="flex items-center gap-1">
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${i <= step ? 'bg-emerald-600 text-white' : 'bg-white/10 text-slate-500'}`}>{i + 1}</div>
              {i < 2 && <div className="w-6 h-px bg-white/10" />}
            </div>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs text-slate-500 mb-1 block">From Branch</label>
          <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-300 text-sm">Warehouse</div>
        </div>
        <div>
          <label className="text-xs text-slate-500 mb-1 block">To Branch</label>
          <div className="bg-white/5 border border-emerald-500/30 rounded-lg px-3 py-2 text-emerald-300 text-sm">Main Branch</div>
        </div>
      </div>
      <div className="bg-slate-900/60 rounded-xl border border-white/5 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-white/5 text-xs font-bold text-slate-400 flex justify-between">
          <span>Select items to transfer</span>
          <span className="text-emerald-400">2 selected</span>
        </div>
        {SAMPLE.products.map((p, i) => (
          <div key={p.sku} className="flex items-center gap-3 px-4 py-2.5 border-b border-white/5 hover:bg-white/[0.02]">
            <div className={`w-4 h-4 rounded border flex items-center justify-center ${i < 2 ? 'bg-emerald-600 border-emerald-600' : 'border-white/20'}`}>
              {i < 2 && <span className="text-white text-[8px]">✓</span>}
            </div>
            <div className="flex-1">
              <p className="text-white text-xs">{p.name}</p>
              <p className="text-slate-500 text-[10px]">In stock: {p.stock} {i < 2 ? '→ Transferring: 10' : ''}</p>
            </div>
            <span className="text-slate-500 text-xs">₱{p.price.toLocaleString()}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <MockBtn label="Cancel" />
        <MockBtn label="Confirm Transfer →" primary />
      </div>
    </PreviewPage>
  );
}

function BIRCompliancePreview() {
  const invoices = [
    { bir: 'INV-MB-001001', ref: 'SI-MB-001041', date: 'May 18', customer: 'Juan dela Cruz',   total: 3120,  vatable: 2785.71, vat: 334.29,  type: 'OR' },
    { bir: 'INV-MB-001002', ref: 'SI-MB-001042', date: 'May 18', customer: 'Walk-in',          total: 8580,  vatable: 7660.71, vat: 919.29,  type: 'OR' },
    { bir: 'INV-MB-001003', ref: 'SI-MB-001043', date: 'May 19', customer: 'Farmers Coop',     total: 25400, vatable: 22678.57,vat: 2721.43, type: 'SI' },
    { bir: 'INV-MB-001004', ref: 'SI-MB-001044', date: 'May 19', customer: 'Pedro Reyes',       total: 5460,  vatable: 4875,    vat: 585,     type: 'OR' },
  ];
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-white font-bold text-base flex items-center gap-2">
            BIR Compliance
            <span className="text-[10px] font-black px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">BIR ACTIVE</span>
          </h2>
          <p className="text-slate-400 text-xs">Official invoice ledger · Main Branch · TIN: 123-456-789-000</p>
        </div>
        <div className="flex gap-2">
          <MockBtn label="Export CSV" sm />
          <MockBtn label="Generate BIR Z-Report" sm />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 mb-4">
        {[['ORs Issued', '4', ''], ['Gross Sales', '₱42,560', 'text-emerald-400'], ['Vatable Sales', '₱38,000', ''], ['VAT (12%)', '₱4,560', 'text-blue-400']].map(([l, v, c]) => (
          <div key={l} className="bg-slate-900/60 border border-white/5 rounded-xl p-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{l}</p>
            <p className={`text-base font-black mt-1 ${c || 'text-white'}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</p>
          </div>
        ))}
      </div>
      <div className="bg-slate-900/60 rounded-xl overflow-hidden border border-white/5">
        <table className="w-full text-xs">
          <thead><TableHeader cols={['BIR Invoice #', 'Internal Ref', 'Date', 'Customer', 'Type', 'Total', 'VAT', 'Status']} /></thead>
          <tbody>
            {invoices.map(inv => (
              <tr key={inv.bir} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-3 py-2 text-emerald-400 font-mono font-bold">{inv.bir}</td>
                <td className="px-3 py-2 text-slate-500 font-mono">{inv.ref}</td>
                <td className="px-3 py-2 text-slate-400">{inv.date}</td>
                <td className="px-3 py-2 text-slate-300">{inv.customer}</td>
                <td className="px-3 py-2"><span className="bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded text-[10px] font-bold">{inv.type}</span></td>
                <td className="px-3 py-2 text-white font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>₱{inv.total.toLocaleString()}</td>
                <td className="px-3 py-2 text-blue-400" style={{ fontVariantNumeric: 'tabular-nums' }}>₱{inv.vat.toFixed(2)}</td>
                <td className="px-3 py-2"><span className="bg-emerald-500/15 text-emerald-400 px-1.5 py-0.5 rounded text-[10px] font-bold">ISSUED</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PreviewPage>
  );
}

function FundManagementPreview() {
  const wallets = [
    { name: 'Cashier Drawer',      type: 'cashier', balance: 5000,    color: '#22c55e', icon: '💵' },
    { name: 'Physical Safe',        type: 'safe',    balance: 136400,  color: '#3b82f6', icon: '🔒' },
    { name: 'Digital / E-Wallet',   type: 'digital', balance: 11520,   color: '#a855f7', icon: '📱' },
    { name: 'Bank Deposit',          type: 'bank',    balance: 450000,  color: '#f59e0b', icon: '🏦' },
  ];
  const transfers = [
    { from: 'Safe', to: 'Cashier', amount: 5000, by: 'Demo Admin', time: '8:05 AM' },
    { from: 'Cashier', to: 'Safe', amount: 22300, by: 'Demo Admin', time: '6:47 PM' },
  ];
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-base">Fund Management</h2>
        <MockBtn label="+ Fund Transfer" primary />
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        {wallets.map(w => (
          <div key={w.type} className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-lg">{w.icon}</span>
              <span className="text-slate-400 text-xs font-semibold">{w.name}</span>
            </div>
            <p className="text-white text-xl font-black" style={{ fontVariantNumeric: 'tabular-nums', color: w.color }}>
              ₱{w.balance.toLocaleString()}
            </p>
          </div>
        ))}
      </div>
      <div className="bg-slate-900/60 rounded-xl border border-white/5 overflow-hidden">
        <div className="px-4 py-2.5 border-b border-white/5 text-xs font-bold text-slate-400">Today's Transfers</div>
        {transfers.map((t, i) => (
          <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-white/5 text-xs">
            <div className="flex items-center gap-2">
              <span className="text-slate-400">{t.from}</span>
              <ChevronRight size={12} className="text-slate-600" />
              <span className="text-emerald-400 font-semibold">{t.to}</span>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-white font-bold">₱{t.amount.toLocaleString()}</span>
              <span className="text-slate-500">{t.by}</span>
              <span className="text-slate-600">{t.time}</span>
            </div>
          </div>
        ))}
      </div>
    </PreviewPage>
  );
}

function SMSGatewayPreview() {
  const convos = [
    { name: 'Maria Santos',   last: 'Your order for NPK Fertilizer is ready for pickup.',   time: '2:35 PM', unread: 0, type: 'outbound' },
    { name: 'Pedro Reyes',    last: 'Your balance of ₱8,500 is now overdue. Please pay.', time: '10:12 AM', unread: 1, type: 'inbound' },
    { name: 'Farmers Coop',   last: 'Thank you! Payment received: ₱6,700. Balance: ₱0.',   time: 'May 22', unread: 0, type: 'outbound' },
    { name: 'Juan dela Cruz', last: 'Auto: Your invoice SI-MB-001041 is paid. Thank you!',   time: 'May 18', unread: 0, type: 'auto' },
  ];
  const [active, setActive] = useState(1);
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-white font-bold text-base">SMS Messages</h2>
        <div className="flex gap-2">
          <span className="text-xs text-emerald-400 bg-emerald-500/10 px-2 py-1 rounded-full font-semibold">✓ Gateway Active</span>
          <MockBtn label="Templates" sm />
        </div>
      </div>
      <div className="flex gap-3 h-64">
        {/* Conversations list */}
        <div className="w-2/5 bg-slate-900/60 rounded-xl border border-white/5 overflow-hidden">
          {convos.map((c, i) => (
            <div key={c.name} onClick={() => setActive(i)}
              className={`flex items-center gap-2.5 px-3 py-2.5 border-b border-white/5 cursor-pointer ${i === active ? 'bg-white/5' : 'hover:bg-white/[0.02]'}`}>
              <div className="w-8 h-8 rounded-full bg-emerald-800 flex items-center justify-center text-white text-xs font-bold shrink-0">
                {c.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center">
                  <span className="text-white text-xs font-semibold truncate">{c.name}</span>
                  <span className="text-slate-600 text-[10px] shrink-0">{c.time}</span>
                </div>
                <p className="text-slate-500 text-[10px] truncate">{c.last}</p>
              </div>
              {c.unread > 0 && <span className="w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0">{c.unread}</span>}
            </div>
          ))}
        </div>
        {/* Chat */}
        <div className="flex-1 bg-slate-900/60 rounded-xl border border-white/5 flex flex-col">
          <div className="px-4 py-2.5 border-b border-white/5 flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-emerald-800 flex items-center justify-center text-white text-xs font-bold">{convos[active].name[0]}</div>
            <span className="text-white text-sm font-semibold">{convos[active].name}</span>
          </div>
          <div className="flex-1 p-3 space-y-2 overflow-auto">
            <div className="flex justify-end">
              <div className="bg-emerald-700/40 rounded-xl px-3 py-2 max-w-[80%] text-xs text-white">{convos[active].last}</div>
            </div>
            {active === 1 && (
              <div className="flex justify-start">
                <div className="bg-white/5 rounded-xl px-3 py-2 max-w-[80%] text-xs text-slate-300">Okay, will pay tomorrow po.</div>
              </div>
            )}
          </div>
          <div className="px-3 pb-3 flex gap-2">
            <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-slate-500 text-xs">Type a message…</div>
            <MockBtn label="Send" sm primary />
          </div>
        </div>
      </div>
    </PreviewPage>
  );
}

function AuditCenterPreview() {
  const findings = [
    { id: 'AUD-001', severity: 'high',   type: 'Discrepancy',        desc: 'Cash count ₱250 over expected. Shift: SHIFT-MB-00008',  time: '6:47 PM', cashier: 'Ana Reyes' },
    { id: 'AUD-002', severity: 'medium', type: 'Price Change',       desc: 'Retail price changed from ₱1,560 → ₱1,490 without approval', time: '2:15 PM', cashier: 'Demo Admin' },
    { id: 'AUD-003', severity: 'low',    type: 'Late Expense',       desc: 'Expense logged 3 days after date. ₱850 Office Supplies', time: 'May 22', cashier: 'Ben Santos' },
  ];
  const SEV = { high: 'bg-red-500/20 text-red-400', medium: 'bg-amber-500/20 text-amber-400', low: 'bg-blue-500/20 text-blue-400' };
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-white font-bold text-base">Audit Center</h2>
          <p className="text-slate-400 text-xs mt-0.5">Discrepancy detection · Transaction verification · Full audit trail</p>
        </div>
        <div className="flex gap-2">
          <div className="text-xs text-red-400 bg-red-500/10 px-3 py-1.5 rounded-full font-semibold">1 High • 1 Medium</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[['Audit Score', '87/100', 'text-emerald-400'], ['Findings', '3', 'text-amber-400'], ['Verified', '156 tx', 'text-blue-400']].map(([l, v, c]) => (
          <div key={l} className="bg-slate-900/60 border border-white/5 rounded-xl p-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{l}</p>
            <p className={`text-lg font-black mt-1 ${c}`}>{v}</p>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {findings.map(f => (
          <div key={f.id} className="bg-slate-900/60 border border-white/5 rounded-xl px-4 py-3 flex items-start gap-3">
            <span className={`text-[10px] font-black px-2 py-0.5 rounded-full mt-0.5 uppercase ${SEV[f.severity]}`}>{f.severity}</span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="text-white text-xs font-semibold">{f.type}</span>
                <span className="text-slate-600 text-[10px]">{f.time}</span>
              </div>
              <p className="text-slate-400 text-xs mt-0.5">{f.desc}</p>
              <p className="text-slate-600 text-[10px] mt-0.5">Cashier: {f.cashier}</p>
            </div>
            <MockBtn label="Investigate" sm />
          </div>
        ))}
      </div>
    </PreviewPage>
  );
}

function AdvancedReportsPreview() {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May'];
  const revenue = [85000, 92000, 78000, 103000, 97000];
  const maxVal = Math.max(...revenue);
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-base">Advanced Reports</h2>
        <MockBtn label="Export PDF" sm />
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {[['Monthly Revenue', '₱97,000', 'text-emerald-400'], ['COGS', '₱71,200', 'text-red-400'], ['Gross Profit', '₱25,800', 'text-blue-400']].map(([l, v, c]) => (
          <div key={l} className="bg-slate-900/60 border border-white/5 rounded-xl p-3">
            <p className="text-[10px] text-slate-500 uppercase tracking-wider">{l}</p>
            <p className={`text-base font-black mt-1 ${c}`} style={{ fontVariantNumeric: 'tabular-nums' }}>{v}</p>
          </div>
        ))}
      </div>
      {/* Bar chart mock */}
      <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4 mb-3">
        <p className="text-xs font-semibold text-slate-400 mb-3">Revenue Trend (Jan–May 2026)</p>
        <div className="flex items-end gap-3 h-28">
          {months.map((m, i) => (
            <div key={m} className="flex-1 flex flex-col items-center gap-1">
              <span className="text-[9px] text-emerald-400" style={{ fontVariantNumeric: 'tabular-nums' }}>
                ₱{Math.round(revenue[i] / 1000)}k
              </span>
              <div
                className="w-full rounded-t-md"
                style={{ height: `${(revenue[i] / maxVal) * 80}px`, backgroundColor: i === months.length - 1 ? '#22c55e' : '#166534' }}
              />
              <span className="text-[9px] text-slate-500">{m}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
        <div className="px-4 py-2.5 border-b border-white/5 text-xs font-bold text-slate-400">Top Products by Revenue</div>
        {SAMPLE.products.slice(0, 3).map((p, i) => (
          <div key={p.sku} className="flex items-center gap-3 px-4 py-2 border-b border-white/5">
            <span className="text-slate-500 text-xs w-4">{i + 1}</span>
            <span className="text-slate-300 text-xs flex-1">{p.name}</span>
            <div className="flex-1 bg-white/5 rounded-full h-1.5 max-w-[80px]">
              <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${90 - i * 25}%` }} />
            </div>
            <span className="text-white text-xs font-semibold" style={{ fontVariantNumeric: 'tabular-nums' }}>₱{((3 - i) * 28000).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </PreviewPage>
  );
}

function EmployeeManagementPreview() {
  return (
    <PreviewPage>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-bold text-base">Employees</h2>
        <MockBtn label="+ Add Employee" primary />
      </div>
      <div className="space-y-2">
        {SAMPLE.employees.map(emp => (
          <div key={emp.name} className="bg-slate-900/60 border border-white/5 rounded-xl px-4 py-3.5 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-emerald-800 flex items-center justify-center text-white font-bold">{emp.name[0]}</div>
            <div className="flex-1">
              <p className="text-white font-semibold text-sm">{emp.name}</p>
              <p className="text-slate-500 text-xs">{emp.role}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Cash Advance This Month</p>
              <p className="text-white font-bold text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                ₱{emp.advance.toLocaleString()}
                <span className="text-slate-600 font-normal text-xs"> / ₱{emp.limit.toLocaleString()}</span>
              </p>
              <div className="w-32 bg-white/5 rounded-full h-1 mt-1">
                <div className="bg-amber-500 h-1 rounded-full" style={{ width: `${(emp.advance / emp.limit) * 100}%` }} />
              </div>
            </div>
            <MockBtn label="View" sm />
          </div>
        ))}
      </div>
    </PreviewPage>
  );
}

/* ── Preview registry ───────────────────────────────────────────────────── */
const FEATURE_PREVIEWS = {
  purchase_orders:       { component: PurchaseOrdersPreview,  plan: 'Business',     desc: 'Automate stock procurement — create POs, auto-receive inventory, track outstanding payables.' },
  supplier_management:   { component: PurchaseOrdersPreview,  plan: 'Business',     desc: 'Manage all your suppliers, payment terms, and purchase history in one place.' },
  pay_supplier:          { component: PurchaseOrdersPreview,  plan: 'Business',     desc: 'Pay suppliers from any wallet. Accounts payable auto-reduces on every payment.' },
  branch_transfers:      { component: BranchTransfersPreview, plan: 'Professional', desc: 'Move stock between branches instantly with full cost tracking and transfer pricing.' },
  multi_branch:          { component: BranchTransfersPreview, plan: 'Professional', desc: 'Manage multiple store branches with a consolidated owner dashboard.' },
  bir_compliance:        { component: BIRCompliancePreview,   plan: 'Professional', desc: 'Issue official BIR Official Receipts and Sales Invoices. Required for BIR-registered businesses.' },
  full_fund_management:  { component: FundManagementPreview,  plan: 'Business',     desc: 'Track Cashier, Safe, Digital (GCash/Maya), and Bank wallets separately. Full movement audit trail.' },
  sms_gateway:           { component: SMSGatewayPreview,      plan: 'Business',     desc: 'Send unlimited SMS via your own SIM. Auto-notify customers on sales, payments, and overdue AR.' },
  telegram_notifications:{ component: SMSGatewayPreview,      plan: 'Business',     desc: 'Real-time Telegram alerts for every major event — sales, variance, low stock, and more.' },
  audit_center:          { component: AuditCenterPreview,     plan: 'Professional', desc: 'Audit scoring, discrepancy detection, transaction verification. Know exactly what happened and when.' },
  transaction_verification:{ component: AuditCenterPreview,  plan: 'Enterprise',   desc: 'Real-time transaction drift detection and automated reconciliation reports.' },
  advanced_reports:      { component: AdvancedReportsPreview, plan: 'Business',     desc: 'Income statements, AR aging, inventory movement, cash flow — all exportable.' },
  employee_management:   { component: EmployeeManagementPreview, plan: 'Business',  desc: 'Employee profiles, cash advance tracking, monthly limits, and manager approval workflow.' },
  count_sheets:          { component: EmployeeManagementPreview, plan: 'Business',  desc: 'Physical inventory count sheets with variance detection and manager sign-off.' },
  multi_cashier_shifts:  { component: FundManagementPreview,  plan: 'Business',     desc: 'Open simultaneous cashier shifts, track per-cashier over/short, dual-signature close.' },
};

/* ── Main modal component ───────────────────────────────────────────────── */
export default function FeaturePreviewModal({ featureFlag, label, onClose }) {
  const navigate = useNavigate();
  const preview = FEATURE_PREVIEWS[featureFlag];
  const planInfo = PLAN_COLORS[preview?.plan || 'Business'];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="feature-preview-modal"
    >
      <div
        className="w-full max-w-5xl bg-[#0F172A] rounded-3xl overflow-hidden shadow-2xl flex flex-col"
        style={{ maxHeight: '90vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Preview mode header bar */}
        <div
          className="flex items-center justify-between px-5 py-3 shrink-0 border-b"
          style={{ backgroundColor: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)' }}
        >
          <div className="flex items-center gap-3">
            <span className="bg-amber-500 text-black text-[10px] font-black px-2.5 py-0.5 rounded-md uppercase tracking-wider flex items-center gap-1">
              <Eye size={10} /> Preview Mode
            </span>
            <span className="text-white font-bold text-sm">{label}</span>
            <span className="text-slate-500 text-xs hidden sm:block">· Interactive demo with sample data</span>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/5">
            <X size={16} className="text-slate-400" />
          </button>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left panel: Feature info + CTA */}
          <div
            className="w-64 shrink-0 flex flex-col gap-4 p-5 border-r overflow-y-auto"
            style={{ borderColor: 'rgba(255,255,255,0.05)', backgroundColor: 'rgba(0,0,0,0.3)' }}
          >
            {/* Plan badge */}
            <div className="space-y-2">
              <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">Available on</p>
              <div
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black"
                style={{ backgroundColor: planInfo?.light, color: planInfo?.text }}
              >
                <Zap size={11} />
                {preview?.plan || 'Business'} Plan
              </div>
            </div>

            {/* Description */}
            {preview && (
              <p className="text-slate-400 text-xs leading-relaxed">{preview.desc}</p>
            )}

            {/* What you're seeing note */}
            <div
              className="rounded-xl px-3 py-2.5 text-[11px] space-y-1"
              style={{ backgroundColor: 'rgba(245,158,11,0.08)', color: 'rgba(245,158,11,0.7)' }}
            >
              <p className="font-bold">You're in preview mode.</p>
              <p>Actions are blocked. Upgrade to use this feature with your actual data.</p>
            </div>

            {/* CTA */}
            <div className="mt-auto space-y-2">
              <button
                className="w-full py-2.5 rounded-xl text-sm font-black text-white transition-all active:scale-95"
                style={{ backgroundColor: planInfo?.bg }}
                onClick={() => { onClose(); navigate('/upgrade'); }}
                data-testid="feature-preview-upgrade-cta"
              >
                Upgrade to {preview?.plan} →
              </button>
              <button
                onClick={onClose}
                className="w-full py-2 rounded-xl text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                Maybe later
              </button>
            </div>
          </div>

          {/* Right panel: Preview content */}
          <div className="flex-1 overflow-y-auto p-5 bg-[#0d1525]" data-testid="feature-preview-content">
            {preview
              ? <preview.component />
              : (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <Lock size={40} className="text-slate-600" />
                  <p className="text-slate-400 text-sm">Preview not available for this feature.</p>
                  <p className="text-slate-500 text-xs">Upgrade to unlock {label} and see it in action with your own data.</p>
                </div>
              )
            }
          </div>
        </div>
      </div>
    </div>
  );
}
